import { debounce } from "lodash";
import { Sheet } from "@mui/joy";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { ProgramContentType } from "data/BlockComponent";
import Quill from "./textarea/Quill";
import PlainTextarea from "./textarea/PlainTextarea";
import { setProgramContentData } from "logic/logics";
import { theme } from "styles/theme";
import { useRecoilState } from "recoil";
import { invalidTextareaIndexAtom } from "recoil/invalidTextareaIndexAtom";

function TextareaCell({
  placeholder,
  initialHtmlString,
  initialValue,
  isCoachField,
  isCoach,
  blockIndex,
  readOnly,
  setData,
  lineIndex,
  cellIndex,
  editorKey,
  id,
}: {
  placeholder?: string;
  initialHtmlString?: string | null;
  initialValue?: string | null;
  isCoachField?: boolean;
  isCoach?: boolean;
  blockIndex: number;
  readOnly?: boolean;
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
  editorKey?: string;
  id?: string;
}) {
  const readonly = readOnly || (isCoachField && !isCoach);
  const [currentContent, setCurrentContent] = useState<{
    htmlString?: string | null;
    value?: string | null;
  }>({ htmlString: initialHtmlString, value: initialValue });

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const [invalidTextareaIndexList, setInvalidTextareaIndexList] =
    useRecoilState(invalidTextareaIndexAtom);
  const indexKey = `${blockIndex}-${lineIndex}-${cellIndex}`;
  const isInvalidTextarea = invalidTextareaIndexList.includes(indexKey);

  const hadInvalidBorder = useRef(isInvalidTextarea);

  useEffect(() => {
    if (isInvalidTextarea) {
      hadInvalidBorder.current = true;
    }
  }, [isInvalidTextarea]);

  useEffect(() => {
    setCurrentContent({
      value: initialValue ?? null,
      htmlString: initialHtmlString ?? null,
    });
  }, [initialHtmlString, initialValue]);

  const handleChange = (newVal?: string | null, newHtml?: string | null) => {
    setProgramContentData({
      setData,
      blockIndex,
      lineIndex,
      cellIndex,
      newlyAddedData: {
        // htmlString: (newHtml || "").length > 0 ? newHtml : undefined,  // 기존 코드
        // → 아래처럼, newHtml이 null이면 그대로 null, 없는 경우만 undefined
        htmlString:
          newHtml === null
            ? null
            : newHtml && newHtml.trim().length > 0
              ? newHtml
              : undefined,
        value:
          isCoach && typeof newVal === "string"
            ? newVal.replace(/[\n\r]+$/, "") // 코치 필드라면 마지막 개행 제거
            : newVal, // 그 외는 null이든 문자열이든 그대로
      },
    });
  };

  const updateInvalidIndexList = useCallback(
    (val?: string | null) => {
      setInvalidTextareaIndexList((prev) => {
        if (!hadInvalidBorder.current) return prev;
        const trimmed = (val || "").trim();
        const exists = prev.includes(indexKey);
        if (trimmed.length > 0) {
          return exists ? prev.filter((idx) => idx !== indexKey) : prev;
        }
        return exists ? prev : [...prev, indexKey];
      });
    },
    [indexKey, setInvalidTextareaIndexList]
  );

  const debouncedSaveText = useRef(
    debounce((val?: string | null, html?: string | null) => {
      handleChange(val, html);
    }, 200)
  ).current;

  const handleChangeTemp = useCallback(
    (val?: string | null, html?: string | null) => {
      setCurrentContent({ value: val, htmlString: html });
      updateInvalidIndexList(val);
      debouncedSaveText(val, html);
    },
    [debouncedSaveText, updateInvalidIndexList]
  );

  useEffect(() => {
    return () => {
      debouncedSaveText.flush(); // ← cancel() 대신 flush()
    };
  }, [debouncedSaveText]);

  return (
    <Sheet
      id={`textarea-${indexKey}`}
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor:
          !isCoach && isCoachField
            ? "transparent"
            : isCoach && isCoachField
              ? "background.level1"
              : "white",
        px: !isCoach && isCoachField ? 0 : "10px",
        py: !isCoach && isCoachField ? 0 : "12px",

        borderRadius: "6px",
        position: "relative",
        ...(!(!isCoach && isCoachField) && {
          borderWidth: "1.4px",
          borderStyle: "solid",
          borderColor: isInvalidTextarea
            ? theme.vars.palette.Border.HighlightWithAlert
            : isFocused
              ? theme.vars.palette.Border.Highlight
              : theme.vars.palette.Border.Weak,
        }),
      }}
    >
      {isCoach ? (
        <Quill
          placeholder={placeholder}
          initialHtmlString={currentContent.htmlString || ""}
          initialValue={currentContent.value || ""}
          handleChange={(val, htmlString) => {
            // Quill은 일반적으로 빈 문자열을 반환하므로 필요시 null로 변환하기
            handleChangeTemp(
              val === "" ? null : val,
              htmlString === "" ? null : htmlString
            );
          }}
          setIsFocused={setIsFocused}
          editorKey={`${editorKey}`.substring(2)}
          disableInlineToolbar={!isCoach}
          readOnly={readonly}
        />
      ) : (
        <PlainTextarea
          placeholder={placeholder}
          initialValue={currentContent.value || ""}
          handleChange={(val, htmlString) => {
            handleChangeTemp(
              val === "" ? null : val,
              htmlString === "" ? null : htmlString
            );
          }}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
          editorKey={`${editorKey}`.substring(2)}
          emojiDisabled={id === "nickname"}
          readOnly={readonly}
          handleChangeWithoutDebounce={(value, htmlString) => {
            handleChange(value, htmlString);
            updateInvalidIndexList(value);
          }}
        />
      )}
    </Sheet>
  );
}

export default memo(TextareaCell);
